import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  updatePassword,
  updateEmail,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { getActionCodeSettings, AUTH_ACTIONS } from '../config/authConfig';
import { logAuditEvent } from '../firebase/auditService';
import { setupActivityListeners } from '../firebase/authService';
import { initiatePasswordReset } from '../services/authService';
import { 
  hasLoggedLoginEvent, 
  markLoginEventLogged, 
  clearAllLoginTracking,
  validateLoginTracker
} from '../utils/loginEventTracker';
import { 
  initializeSessionPersistence,
  storeAuthState,
  getStoredAuthState,
  getSessionStoredAuthState,
  addAuthStateListener,
  addCleanupFunction,
  cleanupSessionPersistence,
  logoutAllTabs
} from '../utils/sessionPersistence';
// RBAC removed - permission imports deleted
import realtimeDataService from '../services/realtimeDataService';
import { subscribeToPermissionChanges } from '../utils/permissionUpdateNotifier';
import { getDefaultPermissionsForRole } from '../utils/accessControl';
import { loadFeatureTogglesFromSettings, clearFeatureTogglesCache, isFeatureEnabled } from '../utils/featureFlags';
import { FEATURES } from '../features';
import { isPublicPortalPath } from '../utils/publicPortalRoutes';

/** Full page load to login after session end — avoids stale dashboard when SPA state lags Firebase. */
function hardNavigateToLoginAfterFullAppSessionEnd() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (path === '/login' || isPublicPortalPath(path)) {
    return;
  }
  window.location.replace(`${window.location.origin}/login`);
}

const AuthContext = React.createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [userLandingPage, setUserLandingPage] = useState('/dashboard');
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [impersonatingUser, setImpersonatingUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const activityCleanupRef = useRef(null);
  /** Tracks a completed main-app login so we only hard-refresh after a real session ends (not first paint). */
  const lastFullSessionUidRef = useRef(null);
  const sessionPersistenceInitialized = useRef(false);
  const waitingForAuthRestore = useRef(false);
  const initialAuthCheckDone = useRef(false);

  async function signup(email, password) {
    try {
      setAuthError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Send verification email with custom URL
      await sendEmailVerification(
        userCredential.user,
        getActionCodeSettings(AUTH_ACTIONS.VERIFY_EMAIL)
      );
      return userCredential;
    } catch (error) {
      setAuthError(error);
      throw error;
    }
  }

  function login(email, password) {
    setLoading(true);
    setAuthError(null);
    return signInWithEmailAndPassword(auth, email, password)
      .catch(error => {
        setAuthError(error);
        throw error;
      })
      .finally(() => setLoading(false));
  }

  function logout() {
    setLoading(true);
    
    // Log logout event before signing out
    if (currentUser) {
      logAuditEvent({
        eventType: 'login',
        description: `User logged out`,
        action: 'logout',
        userRole: userRole
      }).catch(error => {
        console.error('Error logging logout audit event:', error);
      });
    }
    
    // If we're impersonating, clear that state
    if (impersonatingUser) {
      setImpersonatingUser(null);
      setOriginalUser(null);
    }
    
    // Clear session storage for security
    try {
      sessionStorage.clear();
      console.log('Session storage cleared on logout');
    } catch (error) {
      console.warn('Error clearing session storage:', error);
    }
    
    // Clear cross-tab session data
    storeAuthState(false);
    
    return signOut(auth)
      .finally(() => setLoading(false));
  }

  function resetPassword(email) {
    return initiatePasswordReset(email);
  }

  function updateUserEmail(email) {
    return updateEmail(currentUser, email);
  }

  function updateUserPassword(password) {
    return updatePassword(currentUser, password);
  }

  function sendVerificationEmail() {
    return sendEmailVerification(
      currentUser, 
      getActionCodeSettings(AUTH_ACTIONS.VERIFY_EMAIL)
    );
  }

  // Add a function to refresh the user data
  async function refreshUser() {
    if (auth.currentUser) {
      try {
        // Avoid excessive logging in production
        if (process.env.NODE_ENV !== 'production') {
          console.log("Before reload - displayName:", auth.currentUser.displayName);
        }
        
        // Force reload the user from Firebase Auth
        await auth.currentUser.reload();
        const user = auth.currentUser;
        
        if (process.env.NODE_ENV !== 'production') {
          console.log("After reload - displayName:", user.displayName);
        }
        
        // Get additional user data from Firestore
        const role = await getUserRole(user.uid);
        const { permissions, landingPage } = await fetchUserPermissions(user.uid, role);
        
        // Also get the Firestore user document to ensure we have the latest data
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        let firestoreData = {};
        
        if (userDoc.exists()) {
          firestoreData = userDoc.data();
          if (process.env.NODE_ENV !== 'production') {
            console.log("Firestore data - displayName:", firestoreData.displayName);
          }
        }
        
        // Determine the best displayName to use
        const newDisplayName = user.displayName || 
                              firestoreData.displayName || 
                              `${firestoreData.firstName || ''} ${firestoreData.lastName || ''}`.trim() ||
                              user.email?.split('@')[0] || '';
        
        // Check if we actually need to update the context
        if (currentUser && 
            currentUser.displayName === newDisplayName && 
            currentUser.role === role) {
          if (process.env.NODE_ENV !== 'production') {
            console.log("No changes detected, skipping context update");
          }
          return currentUser;
        }
        
        // Update the context state with the latest user data
        const updatedUser = { 
          ...user, 
          role,
          displayName: newDisplayName
        };
        
        if (process.env.NODE_ENV !== 'production') {
          console.log("Updated user - displayName:", updatedUser.displayName);
        }
        
        setUserRole(role);
        setCurrentUser(updatedUser);
        setUserPermissions(permissions);
        setUserLandingPage(landingPage || '/dashboard');
        
        return updatedUser;
      } catch (error) {
        console.error('Error refreshing user:', error);
        throw error;
      }
    }
    return null;
  }

  async function getUserRole(uid) {
    if (!uid) return null;
    try {
      // Check if this is an anonymous user (used for public portal photo uploads)
      const currentUser = auth.currentUser;
      if (currentUser?.isAnonymous) {
        console.log('Anonymous user detected - checking public session for role');
        // Check public session to determine if contractor or tenant
        try {
          const { getSession } = await import('../utils/publicSessionUtils');
          const session = getSession();
          if (session) {
            if (session.userType === 'contractor') {
              console.log('Anonymous user is a contractor - setting restricted role');
              return 'public_contractor'; // Restricted role that won't give main app access
            } else if (session.userType === 'tenant') {
              console.log('Anonymous user is a tenant - setting restricted role');
              return 'public_tenant'; // Restricted role that won't give main app access
            }
          }
        } catch (sessionError) {
          console.warn('Error checking public session:', sessionError);
        }
        // If no session found, return restricted role
        console.log('Anonymous user with no public session - setting restricted role');
        return 'public_anonymous'; // Restricted role for anonymous users
      }

      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Check if user is suspended
        if (userData.disabled) {
          console.log('User account is suspended:', userData.email);
          // Don't sign out immediately, just return null role
          // The auth state change handler will manage the sign out process
          return null;
        }

        return userData.role;
      }
      
      // User document doesn't exist - this should not happen normally
      // but we'll handle it gracefully
      console.warn(`User document not found for UID: ${uid}`);
      // DO NOT create user document automatically - this was causing contractors/tenants
      // to get administrator roles. Only create user documents during proper signup.
      console.log('User document not found - returning null (user should sign up properly)');
      return null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  }

  // Fetch user permissions from Firestore
  const fetchUserPermissions = async (userId, role) => {
    try {
      // Initialize permissions structure
      let permissions = {
        pages: {},
        features: {},
        actions: {},
        fields: {},
        buttons: {},
        menuItems: {},
        topBarLinks: {}
      };
      
      // Try to get role-based permissions from both possible locations
      if (role) {
        // Try the roles collection first
        const roleDocRef = doc(db, 'roles', role.toLowerCase());
        const roleDocSnap = await getDoc(roleDocRef);
        
        if (roleDocSnap.exists() && roleDocSnap.data().permissions) {
          const roleData = roleDocSnap.data();
          const rolePerms = roleData.permissions;
          // Merge role permissions (deep merge for nested objects)
          permissions.pages = { ...permissions.pages, ...(rolePerms.pages || {}) };
          permissions.features = { ...permissions.features, ...(rolePerms.features || {}) };
          permissions.actions = { ...permissions.actions, ...(rolePerms.actions || {}) };
          permissions.fields = { ...permissions.fields, ...(rolePerms.fields || {}) };
          permissions.buttons = { ...permissions.buttons, ...(rolePerms.buttons || {}) };
          permissions.menuItems = { ...permissions.menuItems, ...(rolePerms.menuItems || {}) };
          permissions.topBarLinks = { ...permissions.topBarLinks, ...(rolePerms.topBarLinks || {}) };
          const landingPage = roleData.landingPage || '/dashboard';
          console.log(`Fetched permissions from roles/${role}:`, {
            pages: Object.keys(permissions.pages).length,
            features: Object.keys(permissions.features).length,
            landingPage
          });
          return { permissions, landingPage };
        } else {
          // Fall back to permissions/roles collection
          const rolePermissionsRef = doc(db, 'permissions', 'roles');
          const rolePermissionsSnap = await getDoc(rolePermissionsRef);
          
          if (rolePermissionsSnap.exists() && rolePermissionsSnap.data()[role?.toLowerCase()]) {
            const rolePerms = rolePermissionsSnap.data()[role?.toLowerCase()];
            permissions.pages = { ...permissions.pages, ...(rolePerms.pages || {}) };
            permissions.features = { ...permissions.features, ...(rolePerms.features || {}) };
            permissions.actions = { ...permissions.actions, ...(rolePerms.actions || {}) };
            permissions.fields = { ...permissions.fields, ...(rolePerms.fields || {}) };
            permissions.buttons = { ...permissions.buttons, ...(rolePerms.buttons || {}) };
            permissions.menuItems = { ...permissions.menuItems, ...(rolePerms.menuItems || {}) };
            permissions.topBarLinks = { ...permissions.topBarLinks, ...(rolePerms.topBarLinks || {}) };
            console.log(`Fetched permissions from permissions/roles[${role}]:`, permissions);
          } else {
            // Use default permissions for known roles when no Firestore doc exists (e.g. Inspector before admin creates it)
            const defaults = getDefaultPermissionsForRole(role?.toLowerCase());
            if (Object.keys(defaults.pages).length > 0 || Object.keys(defaults.features).length > 0) {
              permissions = { ...permissions, ...defaults };
              console.log(`Using default permissions for role: ${role}`);
            } else {
              console.log(`No permissions found for role: ${role} in either location`);
            }
          }
        }
      }
      
      // STRICT RBAC: User-specific permissions are DISABLED
      // All permissions come from role only - no user overrides allowed
      console.log('🔒 STRICT RBAC: User-specific permission overrides are disabled');
      
      console.log('✅ STRICT RBAC: Role-based permissions only:', {
        pages: Object.keys(permissions.pages).length,
        features: Object.keys(permissions.features).length,
        actions: Object.keys(permissions.actions).length
      });
      return { permissions, landingPage: '/dashboard' };
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return {
        permissions: {
          pages: {},
          features: {},
          actions: {},
          fields: {},
          buttons: {},
          menuItems: {},
          topBarLinks: {}
        },
        landingPage: '/dashboard'
      };
    }
  };

  // Update the impersonateUser function to also fetch permissions
  async function impersonateUser(userId) {
    try {
      // Save the current user as the original user
      setOriginalUser(currentUser);
      
      // Get the user document from Firestore
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      
      // Create an impersonated user object
      const impersonatedUser = {
        uid: userId,
        email: userData.email,
        displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
        role: userData.role,
        isImpersonated: true
      };
      
      // Fetch permissions for the impersonated user
      const { permissions, landingPage } = await fetchUserPermissions(userId, userData.role);
      
      // Update state
      setImpersonatingUser(impersonatedUser);
      setCurrentUser(impersonatedUser);
      setUserRole(userData.role);
      setUserPermissions(permissions);
      setUserLandingPage(landingPage || '/dashboard');
      
      return impersonatedUser;
    } catch (error) {
      console.error('Error impersonating user:', error);
      throw error;
    }
  }

  // Update the stopImpersonating function to reset permissions
  function stopImpersonating() {
    if (originalUser) {
      setCurrentUser(originalUser);
      setUserRole(originalUser.role);
      
      // Reset permissions to the original user's permissions
      fetchUserPermissions(originalUser.uid, originalUser.role)
        .then(({ permissions, landingPage }) => {
          setUserPermissions(permissions);
          setUserLandingPage(landingPage || '/dashboard');
        })
        .catch(error => {
          console.error('Error fetching original user permissions:', error);
        });
      
      setImpersonatingUser(null);
      setOriginalUser(null);
      return true;
    }
    return false;
  }

  useEffect(() => {
    // ⚠️ CRITICAL: Validate login tracking system on initialization ⚠️
    if (!validateLoginTracker()) {
      console.error('Login event tracking system failed validation - duplicate login events may occur!');
    }
    
    // Initialize session persistence for cross-tab functionality
    if (!sessionPersistenceInitialized.current) {
      initializeSessionPersistence();
      sessionPersistenceInitialized.current = true;
    }
    
    // Check initial auth state before Firebase auth state is ready.
    // We check sessionStorage first (persists through refresh, cleared on genuine tab close)
    // to handle the page-refresh case where beforeunload has already wiped localStorage.
    // Falls back to localStorage for the cross-tab new-tab case.
    const checkInitialAuthState = async () => {
      try {
        const sessionAuth = getSessionStoredAuthState();
        const storedAuth = getStoredAuthState();
        const shouldWait = sessionAuth.isLoggedIn || (storedAuth.isLoggedIn && storedAuth.userData);
        if (shouldWait) {
          console.log('Initial auth check: Found logged-in state, waiting for Firebase to restore...');
          waitingForAuthRestore.current = true;
          
          // Wait up to 500ms for Firebase to restore auth state
          // This gives Firebase time to restore the session before we make logout decisions
          let waitTime = 0;
          const maxWaitTime = 500;
          const checkInterval = 50;
          
          while (waitTime < maxWaitTime && !auth.currentUser) {
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            waitTime += checkInterval;
          }
          
          waitingForAuthRestore.current = false;
          
          if (auth.currentUser) {
            console.log('Initial auth check: Firebase auth restored successfully');
          } else {
            console.warn('Initial auth check: Firebase auth not restored — clearing stale session markers');
            storeAuthState(false);
          }
        } else {
          console.log('Initial auth check: No logged-in state found in localStorage');
        }
      } catch (error) {
        console.error('Error checking initial auth state:', error);
      } finally {
        initialAuthCheckDone.current = true;
      }
    };
    
    // Perform initial auth state check
    checkInitialAuthState();
    
    // Set up cross-tab auth state synchronization
    const authStateCleanup = addAuthStateListener((authState, userData) => {
      if (authState === 'logged_in' && userData) {
        console.log('Syncing user data from another tab:', userData);
        // Update local state with data from another tab
        setCurrentUser(userData.user);
        setUserRole(userData.role);
        setUserPermissions(userData.permissions);
      } else if (authState === 'logged_out') {
        console.log('Logout detected from another tab');
        const hadFullAppSession = !!lastFullSessionUidRef.current;
        lastFullSessionUidRef.current = null;
        // Clear local state when logout detected from another tab
        setCurrentUser(null);
        setUserRole(null);
        setUserPermissions(null);
        setUserLandingPage('/dashboard');
        setImpersonatingUser(null);
        setOriginalUser(null);
        if (hadFullAppSession) {
          hardNavigateToLoginAfterFullAppSessionEnd();
        }
      }
    });
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        // Clear any previous auth errors
        setAuthError(null);
        
        if (user) {
          console.log('Auth state changed - User logged in:', user);

          // Check if this is an anonymous user (used for public portal photo uploads)
          // Anonymous users should NOT get full app access
          if (user.isAnonymous) {
            console.log('Anonymous user detected - setting restricted access');
            lastFullSessionUidRef.current = null;
            // Get role from public session if available
            const role = await getUserRole(user.uid);
            
            // For anonymous users, set minimal permissions and don't log login events
            const restrictedUser = {
              ...user,
              role: role || 'public_anonymous',
              displayName: 'Anonymous User',
              isAnonymous: true
            };

            setUserRole(role || 'public_anonymous');
            setCurrentUser(restrictedUser);
            setUserPermissions({
              pages: {},
              features: {},
              actions: {},
              fields: {},
              buttons: {},
              menuItems: {}
            });

            // Don't initialize real-time data service for anonymous users
            // Don't store auth state for anonymous users
            // Don't log login events for anonymous users
            console.log('Anonymous user authenticated - restricted access only');
            return; // Exit early for anonymous users
          }

          // Get user role and permissions (only for non-anonymous users)
          const role = await getUserRole(user.uid);
          console.log('Retrieved user role:', role);

          // If no role found and user is not anonymous, this is an error
          if (!role) {
            console.warn('No role found for authenticated user - user may need to sign up properly');
            // Don't set user data if no role - this prevents unauthorized access.
            // Sign out so LoginPage does not keep redirecting on a Firebase session with no profile.
            setCurrentUser(null);
            setUserRole(null);
            setUserPermissions(null);
            setUserLandingPage('/dashboard');
            await signOut(auth);
            setAuthError(new Error('NO_PROFILE:This login has no user profile. Ask an administrator to add the account.'));
            return;
          }

          // Check if landlord login is disabled and user is a landlord
          if (role === 'landlord' && !isFeatureEnabled(FEATURES.LANDLORD_LOGIN)) {
            console.log('Landlord login is disabled, signing out:', user.email);
            await signOut(auth);
            setAuthError(new Error('LANDLORD_LOGIN_DISABLED:Landlord access is currently disabled. Please contact your agent for assistance.'));
            return;
          }

          // Get additional user data from Firestore first to check suspension
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          let firestoreData = {};

          if (userDoc.exists()) {
            firestoreData = userDoc.data();
            console.log('Firestore user data:', firestoreData);

            // Check if user is suspended
            if (firestoreData.disabled) {
              console.log('User account is suspended, signing out:', firestoreData.email);
              await signOut(auth);
              setAuthError(new Error('SUSPENDED_ACCOUNT:Your account has been suspended. Please contact your agent for assistance.'));
              return;
            }
          }

          const { permissions, landingPage } = await fetchUserPermissions(user.uid, role);
          console.log('Retrieved user permissions:', permissions);

          // Determine the best displayName to use
          const displayName = user.displayName ||
                             firestoreData.displayName ||
                             `${firestoreData.firstName || ''} ${firestoreData.lastName || ''}`.trim() ||
                             user.email?.split('@')[0] || '';

          // Create a complete user object with all necessary data
          const completeUser = {
            ...user,
            role,
            displayName
          };

          console.log('Setting user data:', { role, displayName, permissions });

          setUserRole(role);
          setCurrentUser(completeUser);
          setUserPermissions(permissions);
          setUserLandingPage(landingPage || '/dashboard');
          lastFullSessionUidRef.current = user.uid;

          // Initialize real-time data service for the logged-in user
          realtimeDataService.initialize(completeUser, role, permissions);

          // Store userId in localStorage for feature flag user-based rollouts
          try {
            localStorage.setItem('userId', user.uid);
            console.log('✅ UserId stored in localStorage for feature flags');
          } catch (error) {
            console.warn('Failed to store userId in localStorage:', error);
          }

          // Refresh feature flags cache on login to ensure correct features are enabled/disabled
          try {
            await loadFeatureTogglesFromSettings();
            console.log('✅ Feature flags cache refreshed on login');
          } catch (error) {
            console.warn('Failed to refresh feature flags cache on login:', error);
          }

          // Store auth state for cross-tab sharing
          storeAuthState(true, {
            user: completeUser,
            role: role,
            permissions: permissions
          });
          
          // ⚠️ CRITICAL LOGIN EVENT TRACKING - DO NOT MODIFY WITHOUT UNDERSTANDING IMPLICATIONS ⚠️
          // This prevents duplicate login audit logs on page refresh/auth state restoration
          // ALWAYS use the loginEventTracker utility - NEVER direct sessionStorage access
          if (!hasLoggedLoginEvent(user.uid)) {
            const loginTimestamp = new Date().toISOString();
            markLoginEventLogged(user.uid, loginTimestamp);
            
            try {
              await logAuditEvent({
                eventType: 'login',
                description: `User logged in successfully`,
                action: 'login_success',
                userRole: role,
                loginMethod: 'email_password',
                timestamp: loginTimestamp
              });
              console.log('✅ Login audit event logged for actual login');
              
              // Track login in Google Analytics
              try {
                const { trackLogin, setAnalyticsUserProperties } = await import('../services/analyticsService');
                trackLogin(user.uid, 'email_password');
                setAnalyticsUserProperties({
                  user_role: role || 'user',
                  user_email: user.email || '',
                });
              } catch (analyticsError) {
                console.warn('Analytics tracking failed (non-critical):', analyticsError);
              }
            } catch (error) {
              console.error('Error logging login audit event:', error);
            }
          } else {
            console.log('⏭️ Skipping login audit log - already logged for this session');
          }
        } else {
          // User is null - check if this is a real logout or just Firebase not ready yet
          // This prevents race condition where new tabs log out before Firebase restores auth
          
          // Check localStorage and sessionStorage to see if there's an active session
          const storedAuth = getStoredAuthState();
          const sessionAuth = getSessionStoredAuthState();
          
          // If we're still waiting for initial auth check, don't process logout yet
          if (waitingForAuthRestore.current || !initialAuthCheckDone.current) {
            console.log('Auth state changed - User is null, but waiting for auth restoration or initial check...');
            // Don't proceed with logout yet - wait for initial check to complete
            return;
          }
          
          // If session or localStorage shows logged in, this might be a temporary Firebase state issue
          // (e.g. page reload cleared localStorage in beforeunload before Firebase restored)
          if (storedAuth.isLoggedIn || sessionAuth.isLoggedIn) {
            console.log('Auth state changed - User is null but session storage shows logged in, waiting briefly for Firebase restore...');
            
            // Wait up to 200ms for Firebase to restore
            let waitTime = 0;
            const maxWaitTime = 200;
            const checkInterval = 50;
            
            while (waitTime < maxWaitTime && !auth.currentUser) {
              await new Promise(resolve => setTimeout(resolve, checkInterval));
              waitTime += checkInterval;
            }
            
            // Check again after wait period
            if (auth.currentUser) {
              console.log('Auth state changed - Firebase auth restored after wait, not logging out');
              // Firebase restored, don't proceed with logout
              return;
            }
            
            // Stale session markers with no Firebase user = zombie UI session.
            // That causes permission-denied on every Firestore read while the
            // chrome still shows the user as logged in. Clear the markers and
            // fall through to logout instead of keeping the zombie alive.
            const recheckStoredAuth = getStoredAuthState();
            const recheckSessionAuth = getSessionStoredAuthState();
            if (recheckStoredAuth.isLoggedIn || recheckSessionAuth.isLoggedIn) {
              console.warn('Auth state changed - session markers say logged in but Firebase auth did not restore; clearing stale session');
              storeAuthState(false);
            }
          }
          
          // Both Firebase and localStorage show logged out - proceed with logout
          console.log('Auth state changed - User logged out (confirmed by both Firebase and localStorage)');
          const hadFullAppSession = !!lastFullSessionUidRef.current;
          lastFullSessionUidRef.current = null;
          setCurrentUser(null);
          setUserRole(null);
          setUserPermissions(null);
          setUserLandingPage('/dashboard');
          // Clear impersonation state on logout
          setImpersonatingUser(null);
          setOriginalUser(null);
          // Clear userId from localStorage on logout
          try {
            localStorage.removeItem('userId');
            console.log('✅ UserId removed from localStorage on logout');
          } catch (error) {
            console.warn('Failed to remove userId from localStorage:', error);
          }
          // Clear feature flags cache on logout
          try {
            clearFeatureTogglesCache();
            console.log('✅ Feature flags cache cleared on logout');
          } catch (error) {
            console.warn('Failed to clear feature flags cache on logout:', error);
          }
          // Clear cross-tab session data
          storeAuthState(false);
          // ⚠️ CRITICAL: Clear ALL login event tracking on logout - NEVER REMOVE THIS ⚠️
          clearAllLoginTracking();
          
          // Track logout in Google Analytics
          try {
            const { trackLogout } = await import('../services/analyticsService');
            trackLogout();
          } catch (analyticsError) {
            console.warn('Analytics tracking failed (non-critical):', analyticsError);
          }

          if (hadFullAppSession) {
            hardNavigateToLoginAfterFullAppSessionEnd();
          }
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setAuthError(error);
      } finally {
        setLoading(false);
        setInitializing(false);
        setIsAuthReady(true);
      }
    });

    return () => {
      unsubscribe();
      authStateCleanup();
    };
  }, []);

  // Set up activity listeners when user is logged in
  useEffect(() => {
    if (currentUser && !impersonatingUser) {
      console.log('Setting up activity listeners for logged-in user');
      activityCleanupRef.current = setupActivityListeners();
    } else {
      // Clean up activity listeners when user is logged out
      if (activityCleanupRef.current) {
        console.log('Cleaning up activity listeners');
        activityCleanupRef.current();
        activityCleanupRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (activityCleanupRef.current) {
        activityCleanupRef.current();
        activityCleanupRef.current = null;
      }
    };
  }, [currentUser, impersonatingUser]);

  // Cleanup session persistence when AuthProvider unmounts
  useEffect(() => {
    return () => {
      cleanupSessionPersistence();
    };
  }, []);

  // Listen for role permission changes and refresh user permissions in real-time
  // When an admin updates permissions in Settings > Access Levels, refetch so sidebar updates immediately
  useEffect(() => {
    if (!currentUser || !userRole) {
      return;
    }

    const unsubscribe = subscribeToPermissionChanges(async ({ role: updatedRole, permissions: newPermissions, landingPage: newLandingPage }) => {
      const normalizedUserRole = userRole?.toLowerCase();
      const normalizedUpdatedRole = updatedRole?.toLowerCase();
      if (normalizedUserRole === normalizedUpdatedRole && newPermissions) {
        console.log('🔄 Permissions updated for current user role - refreshing sidebar');
        setUserPermissions(newPermissions);
        if (newLandingPage) {
          setUserLandingPage(newLandingPage);
        }
        // Update realtime data service with new permissions (if supported)
        if (typeof realtimeDataService?.updatePermissions === 'function') {
          realtimeDataService.updatePermissions(newPermissions);
        }
      }
    });

    return () => unsubscribe();
  }, [currentUser, userRole]);

  const value = {
    currentUser,
    userRole,
    userPermissions,
    userLandingPage,
    login,
    signup,
    logout,
    resetPassword,
    updateUserEmail,
    updateUserPassword,
    sendVerificationEmail,
    loading,
    initializing,
    isAuthReady,
    authError,
    impersonateUser,
    stopImpersonating,
    isImpersonating: !!impersonatingUser,
    impersonatingUser,
    originalUser,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!initializing && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
